/**
 * The one-off setup step.
 *
 * Run once against a sheet that came from the old app. It is idempotent: every
 * write is guarded by a check, so running it a second time reports that there
 * is nothing to do. It never deletes anything, never touches the Due days
 * formula column, and never writes to a row that holds no task.
 *
 * What it does, in order:
 *   1. writes the headers for the four columns the app adds (N, O, P, Q)
 *   2. creates the Projects tab if it is missing, with its header row
 *   3. derives the project list from the names already used on task rows,
 *      trimming whitespace so "Norm Shift " and "Norm Shift" become one
 *   4. gives every task row a TaskID, skipping blank spacer rows
 *   5. stamps every task row with the id of its project, and rewrites the name
 *      in column B to the trimmed form
 *   6. sets rows that carry a task but no status to "Not Started"
 */
import { config } from "../config";
import { sheets, type RangeWrite } from "../lib/sheets";
import {
  ADDED_PROJECT_HEADERS,
  ADDED_TASK_HEADERS,
  PROJECTS_FIRST_DATA_ROW,
  PROJECTS_HEADER_ROW,
  PROJECT_HEADERS,
  TASKS_HEADER_ROW,
  TASKS_READ_FIRST,
  TASK_COLUMNS,
  columnNumber,
  hasExplicitStatus,
  projectToRow,
  projectsHeaderRange,
  projectRowRange,
  taskCell,
} from "../lib/schema";
import { suggestColor } from "../lib/palette";
import { listProjects, newProjectId } from "./projectsRepository";
import { readRows, newTaskId } from "./tasksRepository";
import type { Project } from "../types";

export interface SetupPlan {
  missingHeaders: string[];
  missingProjectHeaders: string[];
  createProjectsTab: boolean;
  newProjects: string[];
  rowsNeedingId: number;
  rowsNeedingProjectId: number;
  rowsNeedingStatus: number[];
  totalTaskRows: number;
}

export interface SetupResult extends SetupPlan {
  applied: true;
}

/** Read a cell from a row that starts at the tasks read range's first column. */
function cellText(row: unknown[], letter: string): string {
  const v = row[columnNumber(letter) - columnNumber(TASKS_READ_FIRST)];
  return v === null || v === undefined ? "" : String(v).trim();
}

/** Look at the sheet and work out what setup would change. */
export async function planSetup(token: string): Promise<SetupPlan> {
  const tabs = await sheets.tabTitles(token);
  const createProjectsTab = !tabs.includes(config.projectsTab);

  const headerRow = (
    await sheets.read(
      `${config.tasksTab}!${TASKS_HEADER_ROW}:${TASKS_HEADER_ROW}`,
      token
    )
  )[0] ?? [];

  const missingHeaders = ADDED_TASK_HEADERS.filter(([letter]) => {
    const index = letter.charCodeAt(0) - "A".charCodeAt(0);
    const value = headerRow[index];
    return !value || String(value).trim() === "";
  }).map(([, name]) => name);

  // Columns added to the Projects tab after it first shipped.
  let missingProjectHeaders: string[] = ADDED_PROJECT_HEADERS.map(([, n]) => n);
  if (!createProjectsTab) {
    const projectHeaderRow =
      (
        await sheets.read(
          `${config.projectsTab}!${PROJECTS_HEADER_ROW}:${PROJECTS_HEADER_ROW}`,
          token
        )
      )[0] ?? [];
    missingProjectHeaders = ADDED_PROJECT_HEADERS.filter(([letter]) => {
      const value = projectHeaderRow[letter.charCodeAt(0) - "A".charCodeAt(0)];
      return !value || String(value).trim() === "";
    }).map(([, name]) => name);
  }

  const rows = await readRows(token);
  const existing = createProjectsTab ? [] : await listProjects(token);
  const known = new Set(existing.map((p) => p.name));

  const namesInUse = new Set<string>();
  let rowsNeedingId = 0;
  let rowsNeedingProjectId = 0;
  const rowsNeedingStatus: number[] = [];

  for (const { row, sheetRow } of rows) {
    const name = cellText(row, TASK_COLUMNS.projectName);
    if (name) namesInUse.add(name);
    if (!cellText(row, TASK_COLUMNS.id)) rowsNeedingId++;
    if (!cellText(row, TASK_COLUMNS.projectId)) rowsNeedingProjectId++;
    if (!hasExplicitStatus(row)) rowsNeedingStatus.push(sheetRow);
  }

  return {
    missingHeaders,
    missingProjectHeaders,
    createProjectsTab,
    newProjects: [...namesInUse].filter((n) => !known.has(n)).sort(),
    rowsNeedingId,
    rowsNeedingProjectId,
    rowsNeedingStatus,
    totalTaskRows: rows.length,
  };
}

/** Apply the plan. Safe to run more than once. */
export async function runSetup(token: string): Promise<SetupResult> {
  const plan = await planSetup(token);

  // 1. headers for the added columns
  if (plan.missingHeaders.length) {
    await sheets.batchWrite(
      ADDED_TASK_HEADERS.map(([letter, name]) => ({
        range: `${config.tasksTab}!${letter}${TASKS_HEADER_ROW}`,
        values: [name],
      })),
      token
    );
  }

  // 2. the Projects tab, and any headers added to it since
  if (plan.createProjectsTab) {
    await sheets.addTab(config.projectsTab, token);
    await sheets.batchWrite(
      [{ range: projectsHeaderRange(config.projectsTab), values: PROJECT_HEADERS }],
      token
    );
  } else if (plan.missingProjectHeaders.length) {
    await sheets.batchWrite(
      ADDED_PROJECT_HEADERS.map(([letter, name]) => ({
        range: `${config.projectsTab}!${letter}${PROJECTS_HEADER_ROW}`,
        values: [name],
      })),
      token
    );
  }

  // 3. the project list
  const existing = await listProjects(token);
  const byName = new Map(existing.map((p) => [p.name, p]));
  const taken = existing.map((p) => p.color);
  let nextRow = PROJECTS_FIRST_DATA_ROW + existing.length;

  const rows = await readRows(token);
  const namesInUse = [
    ...new Set(
      rows.map(({ row }) => cellText(row, TASK_COLUMNS.projectName)).filter(Boolean)
    ),
  ].sort();

  const projectWrites: RangeWrite[] = [];
  for (const name of namesInUse) {
    if (byName.has(name)) continue;
    const color = suggestColor(name, taken);
    taken.push(color);
    const project: Project = {
      id: newProjectId(),
      name,
      color,
      archived: false,
      mindmap: "",
      taskFile: "",
      driveFolder: "",
      sheetRow: nextRow,
    };
    byName.set(name, project);
    projectWrites.push({
      range: projectRowRange(config.projectsTab, nextRow),
      values: projectToRow(project),
    });
    nextRow++;
  }
  if (projectWrites.length) await sheets.batchWrite(projectWrites, token);

  // 4, 5, 6. per-row stamping
  const taskWrites: RangeWrite[] = [];
  for (const { row, sheetRow } of rows) {
    const rawName = String(row[columnNumber(TASK_COLUMNS.projectName) - columnNumber(TASKS_READ_FIRST)] ?? "");
    const name = rawName.trim();

    if (!cellText(row, TASK_COLUMNS.id)) {
      taskWrites.push({
        range: taskCell(config.tasksTab, "id", sheetRow),
        values: [newTaskId()],
      });
    }

    const project = name ? byName.get(name) : undefined;
    if (project && !cellText(row, TASK_COLUMNS.projectId)) {
      taskWrites.push({
        range: taskCell(config.tasksTab, "projectId", sheetRow),
        values: [project.id],
      });
    }

    // Collapse the trailing-space duplicates in column B.
    if (rawName !== name && name) {
      taskWrites.push({
        range: taskCell(config.tasksTab, "projectName", sheetRow),
        values: [name],
      });
    }

    if (!hasExplicitStatus(row)) {
      taskWrites.push({
        range: taskCell(config.tasksTab, "status", sheetRow),
        values: ["Not Started"],
      });
    }
  }

  // Chunked so a large backfill does not exceed the request size limit.
  for (let i = 0; i < taskWrites.length; i += 200) {
    await sheets.batchWrite(taskWrites.slice(i, i + 200), token);
  }

  return { ...plan, applied: true };
}

export function setupNeeded(plan: SetupPlan): boolean {
  return (
    plan.missingHeaders.length > 0 ||
    plan.missingProjectHeaders.length > 0 ||
    plan.createProjectsTab ||
    plan.newProjects.length > 0 ||
    plan.rowsNeedingId > 0 ||
    plan.rowsNeedingProjectId > 0 ||
    plan.rowsNeedingStatus.length > 0
  );
}
